import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';

import './CommentList.css';
import ReviewFormWithSave from './ReviewFormWithSave';
import useLocale from '../hooks/useLocale';

const CommentList = forwardRef((props, ref) => {
  const {
    comments,
    selectedCommentId,
    currentParticipantId,
    onCommentClicked,
    onAppendReply,
    onDeleteComment,
    onDeleteReply,
    onChangeCommentStatus,
    onSaveReview,
    onOpenCommentsForAuthor,
    template,
    currentFormValues,
    currentStatus,
    commentsEditable,
    canChangeStatus = false,
    allowReplies = false,
    formEditable,
    viewerRole,
    reviewFormToggleDisabled = false,
    reviewFormToggleDisabledReason = '',
  } = props;

  // Есть ли уже хотя бы один комментарий, открытый автору
  const commentsOpened = (comments || []).some((c) => c.visible_to_author);

  const listContainerRef = useRef(null);
  const [expandedThreads, setExpandedThreads] = useState({});
  // { [commentId]: true/false }
  const [replyTexts, setReplyTexts] = useState({});
  // { [commentId]: 'text' }
  const [activeCommentMenu, setActiveCommentMenu] = useState(null);
  // commentId
  const [activeReplyMenu, setActiveReplyMenu] = useState(null);
  // `${commentId}-${index}`
  // Anchors + dropdown positioning
  const [commentMenuAnchor, setCommentMenuAnchor] = useState(null);
  const [commentMenuOpenUp, setCommentMenuOpenUp] = useState(false);
  const [commentMenuMaxHeight, setCommentMenuMaxHeight] = useState(null);
  const commentMenuDropdownRef = useRef(null);

  const [replyMenuAnchor, setReplyMenuAnchor] = useState(null);
  const [replyMenuOpenUp, setReplyMenuOpenUp] = useState(false);
  const [replyMenuMaxHeight, setReplyMenuMaxHeight] = useState(null);
  const replyMenuDropdownRef = useRef(null);

  const { getLocalizedFullName, getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  // управление отображением
  const [showComments, setShowComments] = useState(true); // комментарии по умолчанию видны
  const [showReviewForm, setShowReviewForm] = useState(false); // форма по умолчанию скрыта
  const [statusFilter, setStatusFilter] = useState('all'); // all | resolved | unresolved
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (reviewFormToggleDisabled && showReviewForm) {
      setShowReviewForm(false);
    }
  }, [reviewFormToggleDisabled, showReviewForm]);

  useImperativeHandle(ref, () => ({
    scrollToComment: (commentId) => {
      setTimeout(() => {
        const container = listContainerRef.current;
        if (!container) return;

        const el = container.querySelector(`#comment-${commentId}`);
        if (el) {
          const header = document.getElementById('user-header');
          const headerHeight = header ? header.offsetHeight : 0;
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const offset = elRect.top - containerRect.top - headerHeight;
          container.scrollTo({ top: offset, behavior: 'smooth' });
          setExpandedThreads((prev) => ({ ...prev, [commentId]: true }));
        } else {
          console.warn('[CommentList] Не найден элемент для commentId=', commentId);
        }
      }, 0);
    },
  }));

  const toggleThread = (commentId) => {
    setExpandedThreads((prev) => ({ ...prev, [commentId]: !prev[commentId] }));
  };

  const isExpanded = (commentId) => !!expandedThreads[commentId];

  const handleCommentClick = (commentId) => {
    if (onCommentClicked) onCommentClicked(commentId);
  };

  const addReply = (commentId) => {
    const reply = replyTexts[commentId];
    if (reply && reply.trim()) {
      if (onAppendReply) onAppendReply({ commentId, reply: reply.trim() });
      setReplyTexts((prev) => ({ ...prev, [commentId]: '' }));
    }
  };

  const toggleCommentMenu = (commentId, anchorEl) => {
    setActiveCommentMenu((prev) => {
      const next = prev === commentId ? null : commentId;
      setCommentMenuAnchor(next ? anchorEl : null);
      setCommentMenuOpenUp(false);
      setCommentMenuMaxHeight(null);
      return next;
    });
  };

  const confirmDeleteComment = (commentId) => {
    if (onDeleteComment) onDeleteComment(commentId);
    setActiveCommentMenu(null);
  };

  const toggleReplyMenu = (commentId, index, anchorEl) => {
    const key = `${commentId}-${index}`;
    setActiveReplyMenu((prev) => {
      const next = prev === key ? null : key;
      setReplyMenuAnchor(next ? anchorEl : null);
      setReplyMenuOpenUp(false);
      setReplyMenuMaxHeight(null);
      return next;
    });
  };

  const confirmDeleteReply = (commentId, replyIndex) => {
    if (onDeleteReply) onDeleteReply({ commentId, replyIndex });
    setActiveReplyMenu(null);
  };

  const toggleStatus = (comment) => {
    const newStatus = comment.status === 'Resolved' ? 'Unresolved' : 'Resolved';
    if (onChangeCommentStatus) {
      onChangeCommentStatus({ commentId: comment.id, newStatus });
    }
  };

  const formatCommentStatusLabel = (status, lang) => {
    const value = (status || '').toLowerCase();

    if (value === 'resolved') {
      return lang === 'ru' ? 'Решён' : 'Resolved';
    }
    if (value === 'unresolved') {
      return lang === 'ru' ? 'Не решён' : 'Unresolved';
    }
    if (!status) {
      return lang === 'ru' ? 'Без статуса' : 'No status';
    }
    return status;
  };

  const handleClickOutside = () => {
    setActiveCommentMenu(null);
    setActiveReplyMenu(null);

    setCommentMenuAnchor(null);
    setReplyMenuAnchor(null);

    setCommentMenuOpenUp(false);
    setReplyMenuOpenUp(false);

    setCommentMenuMaxHeight(null);
    setReplyMenuMaxHeight(null);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (
      !activeCommentMenu ||
      !commentMenuAnchor ||
      !listContainerRef.current ||
      !commentMenuDropdownRef.current
    ) {
      return;
    }

    const containerRect = listContainerRef.current.getBoundingClientRect();
    const anchorRect = commentMenuAnchor.getBoundingClientRect();

    // natural height of rendered dropdown (before applying maxHeight)
    const dropdownEl = commentMenuDropdownRef.current;
    const dropdownHeight = dropdownEl.scrollHeight || dropdownEl.offsetHeight;

    const spaceBelow = containerRect.bottom - anchorRect.bottom;
    const spaceAbove = anchorRect.top - containerRect.top;

    const shouldOpenUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    setCommentMenuOpenUp(shouldOpenUp);

    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    setCommentMenuMaxHeight(Math.floor(Math.max(80, available - 8)));
  }, [activeCommentMenu, commentMenuAnchor]);

  useLayoutEffect(() => {
    if (
      !activeReplyMenu ||
      !replyMenuAnchor ||
      !listContainerRef.current ||
      !replyMenuDropdownRef.current
    ) {
      return;
    }

    const containerRect = listContainerRef.current.getBoundingClientRect();
    const anchorRect = replyMenuAnchor.getBoundingClientRect();

    const dropdownEl = replyMenuDropdownRef.current;
    const dropdownHeight = dropdownEl.scrollHeight || dropdownEl.offsetHeight;

    const spaceBelow = containerRect.bottom - anchorRect.bottom;
    const spaceAbove = anchorRect.top - containerRect.top;

    const shouldOpenUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    setReplyMenuOpenUp(shouldOpenUp);

    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    setReplyMenuMaxHeight(Math.floor(Math.max(60, available - 8)));
  }, [activeReplyMenu, replyMenuAnchor]);

  // --- фильтрация и поиск ---
  const normalizedSearch = searchText.trim().toLowerCase();

  const filteredComments = (comments || [])
    .filter((c) => {
      if (statusFilter === 'resolved') return c.status === 'Resolved';
      if (statusFilter === 'unresolved') return c.status === 'Unresolved';
      return true;
    })
    .filter((c) => {
      if (!normalizedSearch) return true;
      const inTitle = (c.title || '').toLowerCase().includes(normalizedSearch);
      const inThread = (c.thread || []).some((m) =>
        (m.text || '').toLowerCase().includes(normalizedSearch),
      );
      return inTitle || inThread;
    });

  return (
    <div
      className="comment-list"
      ref={listContainerRef}
      onScroll={() => {
        if (activeCommentMenu || activeReplyMenu) {
          setActiveCommentMenu(null);
          setActiveReplyMenu(null);
          setCommentMenuAnchor(null);
          setReplyMenuAnchor(null);
          setCommentMenuOpenUp(false);
          setReplyMenuOpenUp(false);
          setCommentMenuMaxHeight(null);
          setReplyMenuMaxHeight(null);
        }
      }}
    >
      {/* Верхняя панель: две кнопки + ниже фильтр/поиск */}
      <div className="comment-toolbar">
        <div className="toolbar-buttons">
          <button
            type="button"
            className={`toolbar-toggle-btn ${showComments ? 'active' : ''}`}
            onClick={() => setShowComments((prev) => !prev)}
          >
            {showComments
              ? lang === 'ru'
                ? 'Скрыть комментарии'
                : 'Hide comments'
              : lang === 'ru'
                ? 'Показать комментарии'
                : 'Show comments'}
          </button>

          <button
            type="button"
            className={`toolbar-toggle-btn ${showReviewForm ? 'active' : ''}`}
            disabled={reviewFormToggleDisabled}
            title={reviewFormToggleDisabled ? reviewFormToggleDisabledReason : ''}
            onClick={() => {
              if (reviewFormToggleDisabled) return;
              setShowReviewForm((prev) => !prev);
            }}
          >
            {showReviewForm
              ? lang === 'ru'
                ? 'Скрыть отзыв'
                : 'Hide review form'
              : lang === 'ru'
                ? 'Показать отзыв'
                : 'Show review form'}
          </button>

          {viewerRole === 'reviewer' && (
            <button
              type="button"
              className={`toolbar-toggle-btn publish-btn ${commentsOpened ? 'published' : ''}`}
              disabled={commentsOpened || !onOpenCommentsForAuthor}
              onClick={() => {
                if (commentsOpened || !onOpenCommentsForAuthor) return;
                const confirmText =
                  lang === 'ru'
                    ? 'Комментарии станут видны автору. После этого удалять их будет нельзя. Открыть комментарии для автора?'
                    : 'Comments will become visible to the author. After that you will not be able to delete them. Open comments for the author?';
                if (window.confirm(confirmText)) {
                  onOpenCommentsForAuthor();
                }
              }}
            >
              {commentsOpened
                ? lang === 'ru'
                  ? 'Комментарии открыты автору'
                  : 'Comments are visible to author'
                : lang === 'ru'
                  ? 'Открыть комментарии автору'
                  : 'Open comments to author'}
            </button>
          )}
        </div>

        {/* Фильтр и поиск - только когда комментарии показаны */}
        {showComments && (
          <div className="toolbar-filters">
            <div className="toolbar-search">
              <input
                type="text"
                className="comments-search-input"
                placeholder={lang === 'ru' ? 'Поиск' : 'Search'}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">{lang === 'ru' ? 'Все' : 'All'}</option>
              <option value="resolved">
                {lang === 'ru' ? 'Только решённые' : 'Only resolved'}
              </option>
              <option value="unresolved">
                {lang === 'ru' ? 'Только нерешённые' : 'Only unresolved'}
              </option>
            </select>
          </div>
        )}
      </div>

      {/* Список комментариев */}
      {showComments && (
        <div className="comment-list-body">
          {filteredComments.length > 0 ? (
            filteredComments.map((comment) => (
              <div
                key={comment.id}
                id={`comment-${comment.id}`}
                className={`comment-item ${comment.id === selectedCommentId ? 'selected' : ''}`}
                onClick={() => handleCommentClick(comment.id)}
              >
                <div className="comment-header">
                  <div className="header-left">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleThread(comment.id);
                      }}
                      className="expander-button"
                    >
                      {isExpanded(comment.id) ? '▾' : '▸'}
                    </button>
                    <span className="comment-title">{comment.title}</span>
                  </div>

                  <div className="header-right">
                    {/* Статус справа от названия */}
                    <span
                      className={`status-chip ${
                        comment.status === 'Resolved' ? 'resolved' : 'unresolved'
                      } ${canChangeStatus ? 'status-clickable' : ''}`}
                      onClick={(e) => {
                        if (!canChangeStatus) return; // только те, кому разрешено менять статус
                        e.stopPropagation();
                        toggleStatus(comment);
                      }}
                    >
                      {formatCommentStatusLabel(comment.status, lang)}
                    </span>

                    {/* Меню только для тех, кто может редактировать комментарии */}
                    {(commentsEditable || canChangeStatus) && (
                      <div className="menu-container">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCommentMenu(comment.id);
                          }}
                          className="menu-button"
                        >
                          ⋮
                        </button>

                        {activeCommentMenu === comment.id && (
                          <div className="menu-dropdown">
                            {canChangeStatus && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleStatus(comment);
                                  setActiveCommentMenu(null);
                                }}
                                className="status-button"
                              >
                                {lang === 'ru' ? 'Сменить статус' : 'Change status'}
                              </button>
                            )}

                            {/* Удаление - только когда разрешено полное редактирование */}
                            {!comment.visible_to_author && commentsEditable && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDeleteComment(comment.id);
                                }}
                                className="delete-button"
                              >
                                {lang === 'ru' ? 'Удалить' : 'Delete'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {isExpanded(comment.id) && (
                  <div className="comment-body">
                    {comment.thread && comment.thread.length > 0 && (
                      <div className="comment-thread">
                        {comment.thread.map((msg, index) => (
                          <div key={index} className="comment-message">
                            <div className="message-header">
                              <span className="message-author">
                                {(() => {
                                  // 1. Если у сообщения уже есть строковый author
                                  // (например "Вы" или уже собранное имя) - используем его
                                  let label =
                                    (typeof msg.author === 'string' && msg.author.trim()) || '';

                                  // 2. Если нет, пробуем локализовать full_name
                                  if (!label) {
                                    label = getLocalizedFullName(msg.author_full_name) || '';
                                  }

                                  const isMe = msg.creatorId === currentParticipantId;

                                  // 3. Если до сих пор пусто -
                                  // решаем по роли и тому, своё ли это сообщение
                                  if (!label) {
                                    if (viewerRole === 'author') {
                                      // Автор смотрит: он сам и анонимный рецензент
                                      label = isMe
                                        ? lang === 'ru'
                                          ? 'Вы'
                                          : 'You'
                                        : lang === 'ru'
                                          ? 'Анонимный рецензент'
                                          : 'Anonymous reviewer';
                                    } else if (viewerRole === 'reviewer') {
                                      // Рецензент смотрит: он сам и анонимный автор
                                      label = isMe
                                        ? lang === 'ru'
                                          ? 'Вы'
                                          : 'You'
                                        : lang === 'ru'
                                          ? 'Анонимный автор'
                                          : 'Anonymous author';
                                    } else {
                                      // На всякий случай общий fallback
                                      label =
                                        lang === 'ru'
                                          ? 'Анонимный участник'
                                          : 'Anonymous participant';
                                    }
                                  }

                                  return label;
                                })()}
                              </span>
                              <span className="message-time">{msg.time}</span>
                              {commentsEditable && msg.creatorId === currentParticipantId && (
                                <div className="menu-container">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleReplyMenu(comment.id, index, e.currentTarget);
                                    }}
                                    className="menu-button"
                                  >
                                    ⋮
                                  </button>
                                  {activeReplyMenu === `${comment.id}-${index}` && (
                                    <div
                                      ref={replyMenuDropdownRef}
                                      className={`menu-dropdown reply-dropdown${replyMenuOpenUp ? ' open-up' : ''}`}
                                      style={
                                        replyMenuMaxHeight
                                          ? {
                                              maxHeight: `${replyMenuMaxHeight}px`,
                                              overflowY: 'auto',
                                            }
                                          : undefined
                                      }
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          confirmDeleteReply(comment.id, index);
                                        }}
                                        className="delete-button"
                                      >
                                        {lang === 'ru' ? 'Удалить' : 'Delete'}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="message-text">{msg.text}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {(commentsEditable || allowReplies) && (
                      <div className="reply-container">
                        <input
                          type="text"
                          value={replyTexts[comment.id] || ''}
                          onChange={(e) =>
                            setReplyTexts((prev) => ({
                              ...prev,
                              [comment.id]: e.target.value,
                            }))
                          }
                          placeholder={lang === 'ru' ? 'Добавьте ответ.' : 'Add a reply.'}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addReply(comment.id);
                          }}
                        >
                          {lang === 'ru' ? 'Добавить' : 'Add'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="comment-empty">
              {lang === 'ru' ? 'Нет комментариев.' : 'No comments yet.'}
            </div>
          )}
        </div>
      )}

      {/* Форма отзыва - целиком прячется по кнопке сверху */}
      {showReviewForm && (
        <div className="review-form-wrapper">
          <>
            <h3>{lang === 'ru' ? 'Форма отзыва' : 'Review form'}</h3>
            <ReviewFormWithSave
              template={template.blocks}
              onSave={onSaveReview}
              currentValues={currentFormValues}
              currentStatus={currentStatus}
              editable={formEditable}
            />
          </>
        </div>
      )}
    </div>
  );
});

export default CommentList;
